interface FrameJob {
  step: () => boolean;
  ready: () => boolean;
  cancel: (reason: unknown) => void;
}

/** One soft frame budget for scene creation and buffer uploads across all layers. */
export class FrameWorkQueue {
  private readonly jobs = new Set<FrameJob>();

  constructor(private readonly now: () => number = () => performance.now()) {}

  run<T>(
    work: Generator<void, T>,
    signal?: AbortSignal,
    ready: () => boolean = () => true,
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      const cleanup = (): void => {
        this.jobs.delete(job);
        signal?.removeEventListener('abort', abort);
      };
      const job: FrameJob = {
        ready,
        step: () => {
          try {
            const result = work.next();
            if (!result.done) return false;
            cleanup();
            resolve(result.value);
          } catch (error) {
            cleanup();
            reject(error);
          }
          return true;
        },
        cancel: reason => {
          cleanup();
          reject(reason);
        },
      };
      const abort = (): void => job.cancel(new DOMException('Aborted', 'AbortError'));
      if (signal?.aborted) {
        abort();
      } else {
        signal?.addEventListener('abort', abort, { once: true });
        this.jobs.add(job);
      }
    });
  }

  update(): void {
    const deadline = this.now() + 4;
    let paused = 0;
    while (this.jobs.size && this.now() < deadline) {
      const job = this.jobs.values().next().value;
      if (!job) break;
      this.jobs.delete(job);
      if (!job.ready()) {
        this.jobs.add(job);
        if (++paused >= this.jobs.size) break;
      } else {
        paused = 0;
        if (!job.step()) this.jobs.add(job);
      }
    }
  }

  clear(): void {
    for (const job of this.jobs) job.cancel(new DOMException('Aborted', 'AbortError'));
  }
}
